import { useCallback, useMemo } from 'react';
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import { useNavigate } from 'react-router-dom';
import { Column } from 'primereact/column';
import { useTranslation } from 'react-i18next';
import { Tag } from 'primereact/tag';
import { ProductionPlant, ProductionPlantFieldOptions } from '../../api/_generated_';
import AppRoutes from '../../constants/routes';

type Props = {
  items: ProductionPlant[];
  data: ProductionPlantFieldOptions;
};

export default function ProductionPlantTable({ items, data }: Props) {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const onRowClick = useCallback((event: DataTableRowClickEvent) => {
    navigate(`${AppRoutes.PRODUCTION_PLANTS}/${event.data.id}`);
  }, [navigate]);

  const completenessBodyTemplate = (rowData: ProductionPlant) => {
    const value = Math.round(rowData.completeness || 0);

    let severity: 'danger' | 'warning' | 'success';
    if (value < 40) severity = 'danger';
    else if (value < 70) severity = 'warning';
    else severity = 'success';

    return (
      <Tag
        value={`${value}%`}
        severity={severity}
        style={{
          width: '70px',
          textAlign: 'center',
          justifyContent: 'center',
        }}
      />
    );
  };

  const tableData = useMemo(
    () => items.map((item) => {
      const supplyChainType = data.supplyChains?.find(
        (option) => option.code === item.supplyChainType,
      )?.translation || item.supplyChainType;

      return {
        ...item,
        supplyChainType,
      };
    }),
    [items, data],
  );

  return (
    <DataTable
      value={tableData}
      onRowClick={onRowClick}
      breakpoint="991px"
      className="mb-3 link-table"
    >
      <Column field="id" header="ID" />
      <Column field="supplyChainType" header={t('common.label-supply-chain-type')} />
      <Column field="name" header={t('common.label-name')} sortable />
      <Column field="completeness" header={t('common.label-completeness')} sortable body={completenessBodyTemplate} />
    </DataTable>
  );
}
